import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDoubleDownIcon,
} from "@heroicons/react/24/outline";
import LogoWithName from "../assets/images/logo-with-name.png";
import navigation from "../data/mainMenus.js";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); //  (mobile)
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null); //  (desktop)
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={classNames(
        "sticky top-0 z-50 bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 backdrop-blur-md transition-all duration-300 border-b border-gray-700/50",
        isScrolled
          ? "py-4 shadow-2xl bg-opacity-95 backdrop-blur-lg"
          : "py-3 shadow-lg"
      )}
    >
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between">
          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
            {/* Enhanced Mobile menu button */}
            <button
              className="group relative inline-flex items-center justify-center rounded-xl p-3 text-gray-400 hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-600 hover:text-white focus:ring-2 focus:ring-blue-400 focus:outline-none focus:ring-inset transition-all duration-300 transform hover:scale-105 shadow-lg border-0 focus:border-0 active:border-0"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6 transform group-hover:rotate-90 transition-transform duration-300" />
              ) : (
                <Bars3Icon className="block h-6 w-6 transform group-hover:scale-110 transition-transform duration-300" />
              )}
            </button>
          </div>

          {/* Enhanced Logo */}
          <div className="flex flex-1 items-center justify-center sm:items-center sm:justify-start">
            <div className="flex shrink-0 items-center group">
              <Link to={"/"} className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <img
                  alt="Barma Sorig"
                  src={LogoWithName}
                  className={classNames(
                    "relative transition-all duration-300 transform group-hover:scale-105 drop-shadow-lg",
                    isScrolled ? "px-6 h-28 w-auto" : "h-20 w-auto"
                  )}
                />
              </Link>
            </div>
            <div
              className={classNames(
                "hidden sm:ml-8 sm:block",
                isScrolled ? "text-lg" : "text-base"
              )}
            >
              <div className="flex space-x-8">
                {navigation.map((item) =>
                  item.hasDropdown ? (
                    <div
                      key={item.name}
                      className="relative"
                      onMouseEnter={() => setOpenDesktopDropdown(item.name)}
                      onMouseLeave={() => setOpenDesktopDropdown(null)}
                    >
                      {/* Enhanced Dropdown Tab */}
                      <div className="flex items-center group">
                        <Link
                          to={item.to}
                          className={classNames(
                            location.pathname === item.to ||
                              (item.hasDropdown &&
                                location.pathname.startsWith(item.to) &&
                                item.to !== "/aboutPage")
                              ? "text-white bg-gradient-to-r from-blue-500/20 to-purple-500/20 px-4 py-2 rounded-xl"
                              : "text-gray-300 px-4 py-2 rounded-xl hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-600/50",
                            "relative text-sm font-semibold transition-all duration-300 hover:text-white group transform hover:scale-105"
                          )}
                        >
                          {item.name}
                          {/* Enhanced animated underline */}
                          <span className="absolute bottom-1 left-4 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 transition-all duration-300 group-hover:w-[calc(100%-2rem)] rounded-full"></span>
                          {/* Enhanced active underline */}
                          {(location.pathname === item.to ||
                            (item.hasDropdown &&
                              location.pathname.startsWith(item.to) &&
                              item.to !== "/aboutPage")) && (
                            <span className="absolute bottom-1 left-4 w-[calc(100%-2rem)] h-0.5 bg-gradient-to-r from-white to-blue-200 rounded-full"></span>
                          )}
                          {/* Enhanced arrow icon */}
                          {item.hasDropdown && (
                            <ChevronDoubleDownIcon
                              className={classNames(
                                "ml-2 h-4 w-4 transition-all duration-300 inline text-blue-400",
                                openDesktopDropdown === item.name
                                  ? "rotate-180 text-purple-400"
                                  : "",
                                "group-hover:rotate-180 group-hover:text-purple-400"
                              )}
                            />
                          )}
                        </Link>
                      </div>

                      {/* Enhanced Drop Down Menus */}
                      {openDesktopDropdown === item.name && (
                        <div className="absolute left-0 top-full w-96 origin-top-left rounded-2xl bg-gradient-to-br from-white via-gray-50 to-white py-6 px-6 shadow-2xl ring-1 ring-black/10 z-10 border border-gray-200 backdrop-blur-sm animate-dropdown-in">
                          <div
                            className={
                              item.name === "About Us" || item.name === "Career"
                                ? "flex flex-col gap-3"
                                : "grid grid-cols-2 gap-6"
                            }
                          >
                            {item.name === "About Us" || item.name === "Career"
                              ? item.dropdownItems.map((mainService) => (
                                  <Link
                                    key={mainService.name}
                                    to={mainService.to}
                                    className="group block px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-700 hover:border-l-4 hover:border-blue-500 rounded-xl transition-all duration-300 flex items-center justify-between transform hover:scale-105 hover:shadow-lg"
                                    onClick={() => setOpenDesktopDropdown(null)}
                                  >
                                    <span>{mainService.name}</span>
                                    <ChevronRightIcon className="h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 text-blue-500" />
                                  </Link>
                                ))
                              : item.dropdownItems.map((mainService) => (
                                  <div
                                    key={mainService.name}
                                    className="space-y-2"
                                  >
                                    <h3 className="text-gray-800 font-bold text-sm mb-3 px-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-700 to-blue-600">
                                      {mainService.name}
                                    </h3>
                                    {mainService.subServices ? (
                                      <div className="space-y-2">
                                        {mainService.subServices.map(
                                          (subService) => (
                                            <Link
                                              key={subService.name}
                                              to={subService.to}
                                              className="group block px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-blue-700 hover:border-l-4 hover:border-blue-500 rounded-lg transition-all duration-300 flex items-center justify-between transform hover:scale-105"
                                              onClick={() =>
                                                setOpenDesktopDropdown(null)
                                              }
                                            >
                                              <span>{subService.name}</span>
                                              <ChevronRightIcon className="h-3 w-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 text-blue-500" />
                                            </Link>
                                          )
                                        )}
                                      </div>
                                    ) : (
                                      <Link
                                        to={mainService.to}
                                        className="block px-4 py-2 text-sm text-gray-500 hover:bg-gradient-to-r hover:from-gray-100 hover:to-blue-50 hover:text-gray-700 rounded-lg transition-all duration-300"
                                        onClick={() =>
                                          setOpenDesktopDropdown(null)
                                        }
                                      >
                                        {mainService.name}
                                      </Link>
                                    )}
                                  </div>
                                ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.to}
                      className={classNames(
                        location.pathname === item.to
                          ? "text-white bg-gradient-to-r from-blue-500/20 to-purple-500/20 px-4 py-2 rounded-xl"
                          : "text-gray-300 px-4 py-2 rounded-xl hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-600/50",
                        "relative text-sm font-semibold transition-all duration-300 hover:text-white group transform hover:scale-105"
                      )}
                    >
                      {item.name}
                      {/* Enhanced animated underline */}
                      <span className="absolute bottom-1 left-4 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 transition-all duration-300 group-hover:w-[calc(100%-2rem)] rounded-full"></span>
                      {/* Enhanced active underline */}
                      {location.pathname === item.to && (
                        <span className="absolute bottom-1 left-4 w-[calc(100%-2rem)] h-0.5 bg-gradient-to-r from-white to-blue-200 rounded-full"></span>
                      )}
                    </Link>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Book Appointment btn */}
          <div className="absolute inset-y-0 right-0 flex text-center items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            <Link
              to="/booking"
              className="group relative bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 hover:from-blue-700 hover:via-purple-700 hover:to-blue-800 text-white font-bold py-3 px-6 rounded-xl shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-800 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <span className="relative flex items-center">
                Book Now
                <i className="fas fa-calendar-plus ml-2 transform group-hover:scale-110 transition-transform duration-300"></i>
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Enhanced Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden animate-mobile-menu-in">
          <div className="space-y-2 px-4 pt-4 pb-6 bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-lg border-t border-gray-700/50">
            {navigation.map((item) =>
              item.hasDropdown ? (
                <div key={item.name} className="space-y-2">
                  {/* Enhanced Mobile menu item with arrow */}
                  <div className="flex items-center w-fit">
                    <button
                      className="group text-gray-300 hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-600 hover:text-white rounded-xl px-4 py-3 text-base font-semibold transition-all duration-300 flex items-center transform hover:scale-105 shadow-lg hover:shadow-xl border-none focus:outline-none"
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === item.name ? null : item.name
                        )
                      }
                    >
                      {item.name}
                      <ChevronDoubleDownIcon
                        className={classNames(
                          "ml-2 h-4 w-4 transition-all duration-300 text-blue-400",
                          openDropdown === item.name
                            ? "rotate-180 text-purple-400"
                            : ""
                        )}
                      />
                    </button>
                  </div>

                  {/* Enhanced Mobile dropdown content */}
                  {openDropdown === item.name && (
                    <div className="pl-6 mt-3 space-y-2 animate-mobile-dropdown-in">
                      {item.name === "About Us" || item.name === "Career"
                        ? item.dropdownItems.map((mainService) => (
                            <Link
                              key={mainService.name}
                              to={mainService.to}
                              className="block px-4 py-3 text-sm font-semibold text-gray-300 hover:bg-gradient-to-r hover:from-blue-600/20 hover:to-purple-600/20 hover:text-white rounded-lg transition-all duration-300 transform hover:scale-105 hover:translate-x-2 border-l-2 border-transparent hover:border-blue-400"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {mainService.name}
                            </Link>
                          ))
                        : item.dropdownItems.map((mainService) => (
                            <div key={mainService.name} className="space-y-2">
                              <h3 className="text-gray-400 font-bold text-sm mb-2 px-4 text-transparent bg-clip-text bg-gradient-to-r from-gray-300 to-blue-400">
                                {mainService.name}
                              </h3>
                              <div className="space-y-1 pl-2">
                                {mainService.subServices &&
                                  mainService.subServices.map((subService) => (
                                    <Link
                                      key={subService.name}
                                      to={subService.to}
                                      className="block px-4 py-2 text-sm text-gray-400 hover:bg-gradient-to-r hover:from-blue-600/10 hover:to-purple-600/10 hover:text-white rounded-lg transition-all duration-300 transform hover:scale-105 hover:translate-x-1 border-l border-transparent hover:border-blue-400"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      {subService.name}
                                    </Link>
                                  ))}
                              </div>
                            </div>
                          ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.to}
                  className="block text-gray-300 hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-600 hover:text-white rounded-xl px-4 py-3 text-base font-semibold transition-all duration-300 transform hover:scale-105 hover:translate-x-2 shadow-lg hover:shadow-xl"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>
        </div>
      )}

      {/* Custom styles */}
      <style jsx>{`
        button {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }

        button:focus {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }

        button:active {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }

        a {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }

        a:focus {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }

        a:active {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }

        /* Remove any webkit appearance */
        button,
        a {
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
        }

        @keyframes dropdown-in {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes mobile-menu-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes mobile-dropdown-in {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-dropdown-in {
          animation: dropdown-in 0.2s ease-out forwards;
        }

        .animate-mobile-menu-in {
          animation: mobile-menu-in 0.3s ease-out forwards;
        }

        .animate-mobile-dropdown-in {
          animation: mobile-dropdown-in 0.2s ease-out forwards;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-dropdown-in,
          .animate-mobile-menu-in,
          .animate-mobile-dropdown-in {
            animation: none;
          }
        }
      `}</style>
    </nav>
  );
}
